// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { ChefHat } from "lucide-react"
import type { MealInstance, PreparedBatch } from "@bitebalance/contracts"
import { useI18n, formatMinutes } from "@/i18n"
import { Button } from "@/components/ui/button"
import { humanizeRecipeId } from "./recipeDisplay"
import { preparedStatusText } from "./preparedView"

export interface BatchCardProps {
  batchComponentId: string
  meals: MealInstance[]
  /**
   * ADR-065: the prepared batch backing this plan batch, when the user has
   * confirmed cooking it. Null means it is scheduled but NOT cooked — the
   * plan alone never means prepared.
   */
  prepared?: PreparedBatch | null
  /** Offered only while the batch is scheduled and not yet confirmed as cooked. */
  onMarkPrepared?: () => void
  marking?: boolean
}

/**
 * Batch-cooking summary (T12). `MealInstanceSchema` only carries a grouping
 * `batchComponentId` — there is no batch-component schema anywhere in the
 * contract with a prep date, storage/reheat guidance, or total-yield/
 * remaining-portions figures (checked packages/contracts/src/schemas/*.ts).
 * Rather than inventing those fields, this derives only what's actually
 * computable from the grouped `MealInstance` rows: how many planned meals
 * this batch supplies and their combined servings/time. See the final
 * report for the follow-up (a dedicated batch-component endpoint/schema).
 */
export function BatchCard({ batchComponentId, meals, prepared, onMarkPrepared, marking = false }: BatchCardProps) {
  const { t, locale } = useI18n()
  const title = humanizeRecipeId(meals[0]?.recipeId ?? batchComponentId)
  const totalServings = meals.reduce((sum, m) => sum + m.servings, 0)
  const totalActiveMinutes = meals.reduce((sum, m) => sum + m.activeTimeMinutes, 0)

  return (
    <div className="flex gap-3 rounded-bb-xl border border-line bg-paper p-4 shadow-sm">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-bb-lg bg-sky/40">
        <ChefHat className="size-5 text-ink-2" aria-hidden="true" />
      </div>
      <div className="flex flex-1 flex-col gap-2">
        <h3 className="font-semibold text-ink">{t("views.plan.batchCardTitle", { title })}</h3>
        <p className="text-sm text-ink-2">
          {t("views.plan.portionsSupplied", { count: totalServings })} ·{" "}
          {t("views.mealCard.activeTime", { minutes: totalActiveMinutes })}
        </p>
        {prepared ? (
          <div className="flex flex-col gap-0.5" data-testid={`batch-prepared-${batchComponentId}`}>
            <p className="text-sm font-semibold text-green">{preparedStatusText(prepared, t)}</p>
            {/* The whole point of the feature: a skipped or eaten-out meal does not use a portion. */}
            <p className="text-xs text-ink-2">{t("views.plan.preparedKeepsNote")}</p>
          </div>
        ) : onMarkPrepared ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              className="min-h-9"
              disabled={marking}
              onClick={onMarkPrepared}
              data-testid={`batch-mark-prepared-${batchComponentId}`}
            >
              {marking ? t("views.plan.markPreparedBusy") : t("views.plan.markPrepared")}
            </Button>
            <span className="text-xs text-ink-2">{t("views.plan.markPreparedHint")}</span>
          </div>
        ) : null}
        <ul className="flex flex-wrap gap-1.5 text-xs text-ink-2">
          {meals.map(m => (
            <li key={m.id} className="rounded-bb-full border border-line bg-cream px-2 py-0.5">
              {m.date} · {formatMinutes(m.totalTimeMinutes, locale)}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
